import React, { FormEvent, useEffect, useMemo, useState } from "react";

import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { fetchPolicies, fetchPolicyDetail, updatePolicy } from "../../store/slices/policiesSlice";
import { PolicyType } from "../../types/admin";
import { formatDate } from "../../utils/admin";

const policyTypeLabelMap: Record<PolicyType, string> = {
  age: "만 14세 이상",
  terms: "이용약관",
  privacy: "개인정보처리방침",
  third_party: "개인정보 제3자 제공 동의",
  marketing: "마케팅 정보 수신 동의",
};

const PoliciesPage = () => {
  const dispatch = useAppDispatch();
  const { list, loadingList, listError, current, loadingCurrent, currentError, updating, updateError } =
    useAppSelector((state) => state.policies);

  const [selectedType, setSelectedType] = useState<PolicyType>("age");
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");

  useEffect(() => {
    dispatch(fetchPolicies());
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchPolicyDetail(selectedType));
  }, [dispatch, selectedType]);

  useEffect(() => {
    if (current) {
      setTitle(current.title || "");
      setContent(current.content || "");
    }
  }, [current]);

  const selectedMeta = useMemo(
    () => list.find((policy) => policy.policy_type === selectedType) || null,
    [list, selectedType]
  );

  const getSidebarLabel = (policyType: PolicyType) => {
    const meta = list.find((policy) => policy.policy_type === policyType);
    return meta?.title || policyTypeLabelMap[policyType];
  };

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    try {
      await dispatch(updatePolicy({ policyType: selectedType, title, content })).unwrap();
      window.alert("약관/개인정보가 성공적으로 저장되었습니다.");
      dispatch(fetchPolicies());
    } catch (error) {
      window.alert(error as string || "저장에 실패했습니다.");
    }
  };

  return (
    <section className="admin-page">
      <h2 className="section-title">약관/개인정보 설정</h2>

      <div className="policies-layout">
        <aside className="policies-nav">
          {(Object.keys(policyTypeLabelMap) as PolicyType[]).map((policyType) => (
            <button
              key={policyType}
              className={`policy-nav-item ${selectedType === policyType ? "selected" : ""}`}
              type="button"
              onClick={() => setSelectedType(policyType)}
            >
              <span>{getSidebarLabel(policyType)}</span>
              <small>{selectedMeta?.policy_type === policyType && selectedMeta.is_configured ? "설정됨" : ""}</small>
            </button>
          ))}
        </aside>

        <form className="editor-card stretch" onSubmit={onSubmit}>
          <input
            placeholder="문서 제목"
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            disabled={loadingCurrent}
          />
          <textarea
            placeholder="문서 본문"
            value={content}
            onChange={(event) => setContent(event.target.value)}
            disabled={loadingCurrent}
          />
          <div className="editor-footer">
            <p className="footnote-text">
              마지막 수정: {selectedMeta?.updated_at ? formatDate(selectedMeta.updated_at) : "미설정"}
            </p>
            <button className="primary-btn" type="submit" disabled={loadingCurrent || updating}>
              {updating ? "저장 중..." : "저장"}
            </button>
          </div>
        </form>
      </div>

      {loadingList && <p className="loading-text">정책 목록을 불러오는 중입니다...</p>}
      {listError && <p className="error-text">{listError}</p>}
      {currentError && <p className="error-text">{currentError}</p>}
      {updateError && <p className="error-text">{updateError}</p>}
    </section>
  );
};

export default PoliciesPage;
